import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { categoryAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiGrid, FiStar, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useTone } from '../contexts/ToneContext';
import { resolveIcon } from '../utils/iconMap';
import ConfirmModal from '../components/ConfirmModal';
import './Pages.css';

const Categories = () => {
  const { t } = useTone();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | expense | income | bookmarked
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ name: '', category_type: 'expense', color: '#4A90E2', icon: '' });
  const [bookmarkLoading, setBookmarkLoading] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await categoryAPI.getAll();
      setCategories(res.data.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBookmark = async (category) => {
    setBookmarkLoading(category.id);
    try {
      if (category.is_bookmarked) {
        await categoryAPI.unbookmark(category.id);
      } else {
        await categoryAPI.bookmark(category.id, 0);
      }
      // Update local state immediately
      setCategories(prev =>
        prev.map(c =>
          c.id === category.id ? { ...c, is_bookmarked: !c.is_bookmarked } : c
        )
      );
    } catch (error) {
      toast.error('Failed to update bookmark');
    } finally {
      setBookmarkLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      if (editingId) {
        await categoryAPI.update(editingId, formData);
        toast.success('Category updated');
      } else {
        await categoryAPI.create(formData);
        toast.success('Category created');
      }
      setShowModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (cat) => {
    setFormData({ name: cat.name, category_type: cat.category_type, color: cat.color || '#4A90E2', icon: cat.icon || '' });
    setEditingId(cat.id);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await categoryAPI.delete(deleteTarget);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
    } finally {
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category_type: 'expense', color: '#4A90E2', icon: '' });
    setEditingId(null);
  };

  // Sort: bookmarked first, then by name
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.is_bookmarked && !b.is_bookmarked) return -1;
    if (!a.is_bookmarked && b.is_bookmarked) return 1;
    // Within bookmarked, sort by bookmark_order
    if (a.is_bookmarked && b.is_bookmarked) {
      return (a.bookmark_order || 999999) - (b.bookmark_order || 999999);
    }
    return a.name.localeCompare(b.name);
  });

  const filteredCategories = sortedCategories.filter(c => {
    if (filter === 'expense') return c.category_type === 'expense';
    if (filter === 'income') return c.category_type === 'income';
    if (filter === 'bookmarked') return c.is_bookmarked;
    return true;
  });

  const bookmarkedCount = categories.filter(c => c.is_bookmarked).length;

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiGrid style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('categories.title')}</h1>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('categories.add')}
          </button>
        </div>

        {/* Filter tabs */}
        <div className="category-filter-tabs">
          <button className={`category-filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All ({categories.length})
          </button>
          <button className={`category-filter-tab ${filter === 'expense' ? 'active' : ''}`} onClick={() => setFilter('expense')}>
            Expense
          </button>
          <button className={`category-filter-tab ${filter === 'income' ? 'active' : ''}`} onClick={() => setFilter('income')}>
            Income
          </button>
          <button className={`category-filter-tab ${filter === 'bookmarked' ? 'active' : ''}`} onClick={() => setFilter('bookmarked')}>
            <FiStar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Bookmarked ({bookmarkedCount})
          </button>
        </div>

        {filteredCategories.length === 0 ? (
          <EmptyState
            icon={<FiGrid />}
            title={t('categories.no_categories')}
            message={filter === 'bookmarked' ? t('categories.no_bookmarks_msg') : t('categories.create_first_msg')}
          />
        ) : (
          <div className="category-list">
            {filteredCategories.map(cat => (
              <div key={cat.id} className={`category-item ${cat.is_bookmarked ? 'bookmarked' : ''}`}>
                <div className="category-item-left">
                  <button
                    className={`category-bookmark-btn ${cat.is_bookmarked ? 'active' : ''}`}
                    onClick={() => handleToggleBookmark(cat)}
                    disabled={bookmarkLoading === cat.id}
                    title={cat.is_bookmarked ? 'Remove bookmark' : 'Bookmark this category'}
                  >
                    <FiStar />
                  </button>
                  <span className="category-color-dot" style={{ backgroundColor: cat.color || '#888' }} />
                  <div className="category-item-info">
                    <span className="category-item-name">
                      {cat.icon && <span style={{ marginRight: 4 }}>{resolveIcon(cat.icon)}</span>}
                      {cat.name}
                    </span>
                    <span className="category-item-type">{cat.category_type}</span>
                  </div>
                </div>
                <div className="category-item-right">
                  {cat.is_system ? (
                    <span className="category-system-badge">System</span>
                  ) : (
                    <div className="category-item-actions">
                      <button className="btn-icon" onClick={() => handleEdit(cat)} title={t('action.edit')}>
                        <FiEdit2 size={14} />
                      </button>
                      <button className="btn-icon danger" onClick={() => setDeleteTarget(cat.id)} title={t('action.delete')}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingId ? t('categories.edit') : t('categories.create')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('categories.name')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Groceries, Rent, Freelance"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>{t('categories.type')}</label>
                  <select
                    value={formData.category_type}
                    onChange={e => setFormData({ ...formData, category_type: e.target.value })}
                  >
                    <option value="expense">Expense</option>
                    <option value="income">Income</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('categories.color')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={e => setFormData({ ...formData, color: e.target.value })}
                      style={{ width: 40, height: 36, padding: 2, cursor: 'pointer' }}
                    />
                    <span style={{ color: '#7F8C8D', fontSize: '0.85rem' }}>{formData.color}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('categories.icon')} ({t('common.optional')})</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={e => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="e.g. emoji or icon name"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingId ? t('action.update') : t('action.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={t('categories.delete')}
            message={t('categories.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default Categories;
